import { AppState, Auth0Provider } from '@auth0/auth0-react';
import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import './index.scss';
import { App } from './App';
import reportWebVitals from './reportWebVitals';
import {
    BrowserRouter,
    Route,
    Navigate,
    Routes,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { Dashboard } from './routes/dashboard';
import { Topdesk } from './routes/topdesk';
import { DashboardDetail } from './routes/dashboardDetail';
import { Issues } from './routes/issues';
import { RolloutOverview } from './components/Rollout/RolloutOverview';
import { RolloutEnvironment } from './components/Rollout/RolloutEnvironment';
import { RolloutChecklist } from './components/Rollout/RolloutChecklist';
import { ScenarioRunDetail } from './routes/scenarioRunDetail';
import { ScenarioRuns } from './routes/scenarioRuns';
import { getConfig } from './config';
import { AdminOnly } from './components/AdminOnly/AdminOnly';
import { scenarioRunPath, scenarioTestsPath } from './utils/paths';
import { v2Routes } from './v2/routes';

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);

// Please see https://auth0.github.io/auth0-react/interfaces/Auth0ProviderOptions.html
// for a full list of the available properties on the provider
const config = getConfig();

const providerConfig = {
    domain: config.domain,
    clientId: config.clientId,
    ...(config.audience ? { audience: config.audience } : null),
    ...(config.scope ? { scope: config.scope } : null),
    // The only callback URL registered for this client, so every login lands back here.
    redirectUri: window.location.origin,
    /*
        Keep the token cache across a page refresh.

        The default 'memory' cache is empty again after every F5, and the only way back is
        a hidden iframe with prompt=none. That iframe needs a third-party cookie on the
        Auth0 domain, which browsers block more and more often, so the silent call fails
        with login_required and App.tsx turns it into a full redirect. Storing the cache
        means a refresh keeps the session it already had.
    */
    cacheLocation: 'localstorage' as const,
    /*
        Refresh tokens instead of that same iframe once an access token expires. Needs
        "Allow Offline Access" on the API in Auth0; the SDK adds the offline_access scope
        itself. Without that setting it falls back to the iframe route.
    */
    useRefreshTokens: true,
};

/**
 * Auth0Provider plus the route restore that a login redirect would otherwise lose.
 *
 * Auth0 only ever returns to the origin, so the page someone opened has to travel along as
 * `appState` and be put back afterwards. Sits inside BrowserRouter because useNavigate
 * needs the router context.
 */
const AuthProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate();

    const onRedirectCallback = (appState?: AppState) => {
        // replace: true keeps the ?code=&state= URL out of the back button.
        navigate(appState?.returnTo ?? window.location.pathname, {
            replace: true,
        });
    };

    return (
        <Auth0Provider
            {...providerConfig}
            onRedirectCallback={onRedirectCallback}
        >
            {children}
        </Auth0Provider>
    );
};

/** Sends an old /scenarioruns/... link to where the page lives now, below its environment. */
const LegacyScenarioRedirect = () => {
    const { customerID, environment, runId } = useParams();

    return (
        <Navigate
            replace
            to={
                runId
                    ? scenarioRunPath(customerID, environment, runId)
                    : scenarioTestsPath(customerID, environment)
            }
        />
    );
};

root.render(
    <React.StrictMode>
        <BrowserRouter>
            <AuthProvider>
                <Routes>
                    <Route path="/" element={<App />}>
                        <Route index element={<Dashboard />} />
                        <Route path="dashboard" element={<Dashboard />}></Route>
                        <Route
                            path="dashboard/:customerID/:environment"
                            element={<DashboardDetail />}
                        />
                        <Route
                            path="issues"
                            element={
                                <AdminOnly>
                                    <Issues />
                                </AdminOnly>
                            }
                        />
                        <Route
                            path="rollout"
                            element={
                                <AdminOnly>
                                    <RolloutOverview />
                                </AdminOnly>
                            }
                        />
                        <Route
                            path="rollout/checklist"
                            element={
                                <AdminOnly>
                                    <RolloutChecklist />
                                </AdminOnly>
                            }
                        />
                        <Route
                            path="rollout/checklist/:deployId"
                            element={
                                <AdminOnly>
                                    <RolloutChecklist />
                                </AdminOnly>
                            }
                        />
                        <Route
                            path="rollout/:customerID/:environment"
                            element={
                                <AdminOnly>
                                    <RolloutEnvironment />
                                </AdminOnly>
                            }
                        />
                        <Route
                            path="dashboard/:customerID/:environment/scenariotests"
                            element={
                                <AdminOnly>
                                    <ScenarioRuns />
                                </AdminOnly>
                            }
                        />
                        <Route
                            path="dashboard/:customerID/:environment/scenariotests/:runId"
                            element={
                                <AdminOnly>
                                    <ScenarioRunDetail />
                                </AdminOnly>
                            }
                        />
                        {/* The pages used to live here; links to them are in chats and bookmarks. */}
                        <Route
                            path="scenarioruns/:customerID/:environment"
                            element={<LegacyScenarioRedirect />}
                        />
                        <Route
                            path="scenarioruns/:customerID/:environment/:runId"
                            element={<LegacyScenarioRedirect />}
                        />
                        <Route path="topdesk" element={<Topdesk />} />
                        <Route
                            path="*"
                            element={
                                <main style={{ padding: '1rem' }}>
                                    <p>There's nothing here!</p>
                                </main>
                            }
                        />
                    </Route>
                    {/* The redesigned dashboard, next to the old pages until every screen has moved. */}
                    {v2Routes}
                </Routes>
            </AuthProvider>
        </BrowserRouter>
    </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
